import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AuthContext, API } from '../../App';
import { User, Globe, ArrowLeft, Save, LogOut, Shield } from 'lucide-react';
import { toast } from 'sonner';
import NotificationPreferences from '../../components/NotificationPreferences';

export default function ProfileSettings() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, logout } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  const languages = [
    { code: 'en', name: 'English', native: 'English' },
    { code: 'hi', name: 'Hindi', native: 'हिंदी' },
    { code: 'kn', name: 'Kannada', native: 'ಕನ್ನಡ' }
  ];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProfile(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeLanguage = (langCode) => {
    setSelectedLanguage(langCode);
    i18n.changeLanguage(langCode);
    toast.success(`Language changed to ${languages.find(l => l.code === langCode)?.name}`);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // TODO: Save profile updates to backend
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] pb-20">
      {/* Header */}
      <div className="bg-primary text-white p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(user?.role === 'care_provider' ? '/provider-dashboard' : '/mobile-chats')} 
            className="p-1"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-heading font-semibold">{t('profile')}</h1>
        </div>
      </div>

      <div className="container mx-auto max-w-2xl px-4 py-6 space-y-4">
        {/* Profile Info Card */}
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
              <User size={40} className="text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-semibold text-[#2D3748]">
                {profile?.name || user?.name}
              </h2>
              <p className="text-[#718096]">{profile?.email || user?.email}</p>
              <span className="inline-block mt-1 px-3 py-1 bg-primary/10 text-primary text-sm rounded-full capitalize">
                {profile?.role?.replace('_', ' ') || user?.role?.replace('_', ' ')}
              </span>
            </div>
          </div>

          {profile?.role === 'care_provider' && (
            <div className="border-t pt-4 space-y-2">
              {profile.specialization && (
                <div className="flex items-center gap-2 text-sm">
                  <Shield size={16} className="text-primary" />
                  <span className="text-[#718096]">Specialization:</span>
                  <span className="font-medium text-[#2D3748]">{profile.specialization}</span>
                </div>
              )}
              {profile.certification && (
                <div className="flex items-center gap-2 text-sm">
                  <Shield size={16} className="text-primary" />
                  <span className="text-[#718096]">Certification:</span>
                  <span className="font-medium text-[#2D3748]">{profile.certification}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Language Settings */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe size={20} className="text-primary" />
            <h3 className="text-lg font-heading font-semibold text-[#2D3748]">Language Preference</h3>
          </div>
          <div className="space-y-2">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  selectedLanguage === lang.code
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-primary/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-[#2D3748]">{lang.name}</div>
                    <div className="text-sm text-[#718096]">{lang.native}</div>
                  </div>
                  {selectedLanguage === lang.code && (
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Notification preferences (email + WhatsApp opt-in) */}
        {user?.role !== 'care_provider' && <NotificationPreferences />}

        {/* ABHA Integration (if identified user) */}
        {user?.role && (
          <div className="card p-6">
            <h3 className="text-lg font-heading font-semibold text-[#2D3748] mb-2">
              ABHA Health ID
            </h3>
            <p className="text-sm text-[#718096] mb-4">
              Link your Ayushman Bharat Health Account for seamless health records
            </p>
            {profile?.abha_id ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-sm font-medium text-green-900">ABHA Linked</div>
                <div className="text-xs text-green-700 mt-1">{profile.abha_id}</div>
              </div>
            ) : (
              <button className="btn-secondary w-full">
                Link ABHA ID (Coming Soon)
              </button>
            )}
          </div>
        )}

        {/* Account Actions */}
        <div className="card p-6 space-y-3">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-3 text-destructive border border-destructive rounded-xl hover:bg-destructive/5 transition-colors"
          >
            <LogOut size={20} />
            <span className="font-medium">Logout</span>
          </button>
        </div>

        {/* App Info */}
        <div className="text-center text-sm text-[#A0AEC0] py-4">
          <p>Veyonika v1.0</p>
          <p className="mt-1">Your privacy is our priority</p>
        </div>
      </div>

      {/* Bottom Navigation (Mobile) */}
      {user?.role !== 'care_provider' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-2 flex justify-around">
          <button
            onClick={() => navigate('/mobile-chats')}
            className="flex flex-col items-center py-2 px-4 text-[#718096]"
          >
            <User size={24} />
            <span className="text-xs mt-1">{t('chats')}</span>
          </button>
          <button
            onClick={() => navigate('/mobile-care-options')}
            className="flex flex-col items-center py-2 px-4 text-[#718096]"
          >
            <Globe size={24} />
            <span className="text-xs mt-1">{t('care_options')}</span>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center py-2 px-4 text-primary"
          >
            <User size={24} />
            <span className="text-xs mt-1">{t('profile')}</span>
          </button>
        </div>
      )}
    </div>
  );
}